import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ClipboardList, Home, FileText, GitCompareArrows, MessageCircle, Sparkles, PlayCircle, Star, X as XIcon, AlertCircle, Inbox, FileQuestion, CalendarX, Folder, CalendarClock, Users, Mail, Layers, Image as ImageIcon, Briefcase, Shield, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { DEFAULT_MARKETING, useMarketing } from "../lib/marketingContent";
import { useMarketingTracker } from "../lib/marketingTracker";

/* ============================================================
   PorchLight — agent-facing marketing site
   Lives at theporchlight.io (root) and at /for-agents in preview.
   B2B target: real estate agents who want to sell more thoughtfully.

   Copy is sourced from the MarketingProvider (DB overrides on top of
   DEFAULT_MARKETING). Images, icons, and shot arrays remain in code
   because they're either local assets or lucide-react components — the
   admin CMS edits text; engineering edits visuals.
   ============================================================ */

// Spotlight visuals (shots, layout). Text is overlaid from CMS at render time.
const SPOTLIGHT_VISUALS = {
  "signing-spotlight": {
    background: "var(--surface-alt)",
    imageSide: "right",
    shots: [{ src: "/marketing/signing.png", label: "Signed and filed" }],
  },
  "listing-marketing-spotlight": {
    background: "var(--bg)",
    imageSide: "left",
    shots: [{ src: "/marketing/listing_site.png", label: "Listing page" }],
  },
  "listing-activity-spotlight": {
    background: "var(--surface-alt)",
    imageSide: "right",
    shots: [{ src: "/marketing/listing_activity.png",
              label: "What your seller sees" }],
  },
  "prep-guide-spotlight": {
    background: "var(--bg)",
    imageSide: "left",
    shots: [
      { src: "/marketing/prep_guide_repairs.png", label: "Repairs" },
      { src: "/marketing/prep_guide_kitchen.png", label: "Kitchen" },
      { src: "/marketing/prep_guide_bathrooms.png", label: "Bathrooms" },
      { src: "/marketing/prep_guide_living_room.png", label: "Living Room" },
      { src: "/marketing/prep_guide_photo_prep.png", label: "Photo prep" },
    ],
  },
  "net-sheet-spotlight": {
    background: "var(--bg)",
    imageSide: "left",
    shots: [
      { src: "/marketing/net_sheet.png", label: "Single estimate" },
      { src: "/marketing/net_sheet_compare.png", label: "Compare offers" },
    ],
  },
  "important-dates-spotlight": {
    background: "var(--surface-alt)",
    imageSide: "right",
    shots: [{ src: "/marketing/important_dates.png", label: "Timeline" }],
  },
  "agent-portal-spotlight": {
    background: "var(--bg)",
    imageSide: "left",
    shots: [
      { src: "/marketing/agent_portal_sellers.png",  label: "Your sellers" },
      { src: "/marketing/agent_portal_seller.png",   label: "Seller detail" },
      { src: "/marketing/agent_portal_messages.png", label: "Inbox" },
      { src: "/marketing/agent_portal_branding.png", label: "Branding" },
    ],
  },
};

// Icons for the "And a whole lot more" grid — index-aligned with content.more_features.items
const MORE_FEATURE_ICONS = [
  <Folder size={20} />,
  <ClipboardList size={20} />,
  <CalendarClock size={20} />,
  <Users size={20} />,
  <Mail size={20} />,
  <Layers size={20} />,
  <ImageIcon size={20} />,
  <Briefcase size={20} />,
  <Shield size={20} />,
];

// Icons for the "What it does" list — index-aligned with content.what_it_does.items
const WHAT_IT_DOES_ICONS = [
  <Home size={18} />,
  <ClipboardList size={18} />,
  <FileText size={18} />,
  <GitCompareArrows size={18} />,
];

// Icons for the "Sound familiar?" chaos column — index-aligned with content.sound_familiar.chaos
const CHAOS_ICONS = [
  <Inbox size={16} />,
  <FileQuestion size={16} />,
  <CalendarX size={16} />,
  <AlertCircle size={16} />,
];

/* ============================================================
   PorchLight — agent-facing marketing site
   Lives at theporchlight.io (root) and at /for-agents in preview.
   B2B target: real estate agents who want to sell more thoughtfully.
   ============================================================ */

// Build full spotlight objects by merging CMS text with the hard-coded visuals.
function buildSpotlights(content) {
  return (content.spotlights || DEFAULT_MARKETING.spotlights).map((s) => {
    const v = SPOTLIGHT_VISUALS[s.id] || { background: "var(--bg)", imageSide: "right", shots: [] };
    return {
      id: s.id,
      overline: s.overline,
      headline: s.headline,
      italicTail: s.italic_tail,
      body: s.body,
      features: s.features || [],
      background: v.background,
      imageSide: v.imageSide,
      shots: v.shots,
    };
  });
}

export default function MarketingLanding() {
  useMarketingTracker();
  // Lightbox state lives at the root so any spotlight can open it.
  // { spotlightIndex, shotIndex } | null
  const [lightbox, setLightbox] = useState(null);
  const { content } = useMarketing();
  const spotlights = buildSpotlights(content);
  return (
    <div data-testid="marketing-landing" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <MarketingNav />
      <Hero />
      <BuiltByAgent />
      <SoundFamiliar />
      <CategoryPositioning />
      {/* `id="features"` is the anchor target for the nav's "Features" link.
          Wraps the spotlight strip so clicking the nav scrolls right to the
          first product visual instead of stopping at the positioning section. */}
      <div id="features">
        {spotlights.map((s, i) => (
          <FeatureSpotlight
            key={s.id}
            spotlight={s}
            spotlightIndex={i}
            onOpen={(shotIndex) => setLightbox({ spotlightIndex: i, shotIndex })}
          />
        ))}
      </div>
      <AndMore />
      <WhyAgentsUse />
      <Pricing />
      <FAQ />
      <Waitlist />
      <DemoVideo />
      <MarketingFooter />
      {lightbox && (
        <Lightbox
          card={{
            caption: spotlights[lightbox.spotlightIndex].overline,
            emphasis: spotlights[lightbox.spotlightIndex].headline,
            shots: spotlights[lightbox.spotlightIndex].shots,
          }}
          initialShotIndex={lightbox.shotIndex}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

/* ----- Nav (light, agent-pitch) ------------------------------ */
function MarketingNav() {
  // Track which section is currently in view so the matching nav link
  // can highlight ("scroll-spy"). Each id corresponds to a top-level section.
  const SECTION_IDS = ["sound-familiar", "what-it-does", "pricing", "faq"];
  const [active, setActive] = useState(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        // Pick the entry highest on screen that's at least partially visible.
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 },
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const link = (id, label, testid) => (
    <a
      href={`#${id}`}
      data-testid={testid}
      className={`marketing-nav-link${active === id ? " is-active" : ""}`}
    >
      {label}
    </a>
  );

  return (
    <header
      data-testid="marketing-nav"
      style={{
        position: "sticky", top: 0, zIndex: 30,
        background: "rgba(250, 247, 241, 0.85)",
        backdropFilter: "saturate(140%) blur(14px)",
        WebkitBackdropFilter: "saturate(140%) blur(14px)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div className="container-x" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0" }}>
        <a href="#top" style={{ display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none", color: "var(--text)" }}>
          <span style={{
            width: 32, height: 32, borderRadius: 8,
            background: "var(--primary)", color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Home size={16} />
          </span>
          <span className="font-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "-0.01em" }}>PorchLight</span>
        </a>
        <nav className="marketing-nav-links">
          {link("sound-familiar", "Sound familiar?", "nav-problem")}
          {link("features", "Features", "nav-what")}
          {link("pricing", "Pricing", "nav-pricing")}
          {link("faq", "FAQ", "nav-faq")}
          <Link
            to="/signup"
            data-testid="nav-cta"
            className="btn-primary marketing-nav-cta"
            style={{ padding: "10px 18px", fontSize: 14, textDecoration: "none", whiteSpace: "nowrap" }}
          >
            Get started
          </Link>
        </nav>
      </div>
    </header>
  );
}

/* ----- Hero -------------------------------------------------- */
function Hero() {
  const { content } = useMarketing();
  const h = content.hero;
  return (
    <section id="top" style={{ padding: "96px 0 96px", position: "relative", overflow: "hidden" }}>
      <div className="container-x" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)", gap: 80, alignItems: "center" }}>
        <div>
          <span className="overline" data-testid="marketing-hero-overline" style={{ borderTop: "1px solid var(--text)", paddingTop: 10, display: "inline-block" }}>
            {h.overline}
          </span>
          <h1
            data-testid="marketing-hero-h1"
            className="font-serif"
            style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", lineHeight: 1.06, letterSpacing: "-0.02em", margin: "26px 0 16px", fontWeight: 500 }}
          >
            {h.headline}{h.headline_italic ? <> <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>{h.headline_italic}</em></> : null}
          </h1>
          <p style={{ fontSize: "clamp(1.15rem, 1.6vw, 1.4rem)", lineHeight: 1.4, color: "var(--text)", maxWidth: 540, margin: "0 0 18px", fontWeight: 500 }}>
            {h.subhead}
          </p>
          <p style={{ fontSize: "1rem", lineHeight: 1.6, color: "var(--text-muted)", maxWidth: 540, margin: 0 }}>
            {h.body}
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 36, flexWrap: "wrap" }}>
            <Link to="/signup" data-testid="hero-signup" className="btn-primary" style={{ textDecoration: "none" }}>
              {h.cta_primary} <ArrowRight size={16} />
            </Link>
            <a href="#start" data-testid="hero-book-demo" className="btn-secondary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <PlayCircle size={16} /> {h.cta_secondary}
            </a>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 28, flexWrap: "wrap" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--secondary)" }} aria-hidden="true">
              <Star size={14} fill="currentColor" /><Star size={14} fill="currentColor" /><Star size={14} fill="currentColor" /><Star size={14} fill="currentColor" /><Star size={14} fill="currentColor" />
            </div>
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{h.social_proof}</span>
          </div>
        </div>
        <HeroVisual />
      </div>
    </section>
  );
}

function HeroVisual() {
  return (
    <div style={{ position: "relative", aspectRatio: "4/5", borderRadius: 6, overflow: "hidden", background: "var(--surface-alt)" }}>
      <img
        src="/prep-guide/living_room.jpg"
        alt="A bright, organized living room"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
      {/* Floating UI cards layered over the photo */}
      <div
        data-testid="hero-card-progress"
        style={{
          position: "absolute", left: 24, bottom: 24, right: 24,
          background: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
          borderRadius: 14, padding: "14px 16px",
          boxShadow: "0 24px 60px rgba(28, 25, 23, 0.18)",
          display: "flex", alignItems: "center", gap: 14,
        }}
      >
        <div style={{
          width: 38, height: 38, borderRadius: 999,
          background: "var(--primary)", color: "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontWeight: 600, fontSize: 13,
        }}>JP</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Jessica's prep · 78% done</div>
          <div style={{ height: 6, background: "var(--surface-alt)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ width: "78%", height: "100%", background: "linear-gradient(90deg, var(--primary), var(--secondary))", borderRadius: 999 }} />
          </div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--secondary)", letterSpacing: "0.06em", textTransform: "uppercase" }}>On track</span>
      </div>
      <div
        data-testid="hero-card-task"
        style={{
          position: "absolute", left: -16, top: 36,
          background: "#fff", borderRadius: 12, padding: "12px 14px",
          boxShadow: "0 14px 40px rgba(28, 25, 23, 0.15)",
          display: "flex", alignItems: "center", gap: 10, maxWidth: 240,
          border: "1px solid var(--border)",
        }}
      >
        <Check size={16} color="#fff" style={{ background: "var(--primary)", borderRadius: 4, padding: 1 }} />
        <span style={{ fontSize: 13, color: "var(--text-muted)", textDecoration: "line-through" }}>Patch nail holes & repaint</span>
      </div>
      <div
        data-testid="hero-card-message"
        style={{
          position: "absolute", right: -16, top: 120,
          background: "#fff", borderRadius: 12, padding: "12px 14px",
          boxShadow: "0 14px 40px rgba(28, 25, 23, 0.15)",
          display: "flex", alignItems: "center", gap: 10, maxWidth: 240,
          border: "1px solid var(--border)",
        }}
      >
        <span style={{
          width: 28, height: 28, borderRadius: 999, background: "#FBECE8",
          display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--secondary)",
        }}>
          <MessageCircle size={14} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: "var(--text-faint)", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>Agent replied</div>
          <div style={{ fontSize: 13, color: "var(--text)", marginTop: 2 }}>Got it — staging Tuesday 10am 👍</div>
        </div>
      </div>
    </div>
  );
}

/* ----- Credibility ribbon — sits below the hero ------------- */
function BuiltByAgent() {
  const { content } = useMarketing();
  const items = content.built_by_agent || [];
  return (
    <section
      data-testid="built-by-agent"
      style={{
        padding: "28px 0",
        background: "var(--primary)",
        color: "#fff",
        borderTop: "1px solid rgba(255,255,255,0.08)",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div
        className="container-x"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 20,
          alignItems: "center",
        }}
      >
        {items.map((it, i) => (
          <div
            key={i}
            data-testid={`credibility-item-${i}`}
            style={{
              fontSize: "0.95rem",
              lineHeight: 1.4,
              textAlign: "center",
              opacity: 0.95,
            }}
          >
            <span style={{ opacity: 0.75 }}>{it.stat} </span>
            <span className="font-serif" style={{ fontStyle: "italic", letterSpacing: "-0.005em" }}>
              {it.emphasis}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ----- Sound familiar? — pain → solution moment ------------ */
function SoundFamiliar() {
  const { content } = useMarketing();
  const sf = content.sound_familiar;
  const chaos = (sf.chaos || []).map((label, i) => ({
    icon: CHAOS_ICONS[i] || <AlertCircle size={16} />,
    label,
  }));
  const calm = sf.calm || [];
  return (
    <section
      id="sound-familiar"
      data-testid="sound-familiar"
      style={{ padding: "96px 0", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}
    >
      <div className="container-x">
        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 56px" }}>
          <span className="overline" style={{ color: "var(--secondary)" }}>{sf.overline}</span>
          <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4.4vw, 3.2rem)", letterSpacing: "-0.02em", margin: "12px 0 16px", fontWeight: 500, lineHeight: 1.08 }}>
            {sf.headline}{sf.headline_italic ? <> <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>{sf.headline_italic}</em></> : null}
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "1.05rem", lineHeight: 1.6, margin: 0 }}>
            {sf.body}
          </p>
        </div>

        <div className="pain-grid" data-testid="pain-grid">
          {/* Chaos column */}
          <div className="pain-col pain-col-chaos">
            <span className="overline" style={{ color: "var(--text-faint)" }}>Without PorchLight</span>
            <ul style={{ listStyle: "none", padding: 0, margin: "16px 0 0", display: "flex", flexDirection: "column", gap: 12 }}>
              {chaos.map((c, i) => (
                <li
                  key={i}
                  data-testid={`pain-chaos-${i}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "14px 18px",
                    background: "#fff",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    fontSize: 15,
                    color: "var(--text-muted)",
                    transform: `rotate(${i % 2 === 0 ? "-0.4deg" : "0.5deg"})`,
                  }}
                >
                  <span style={{ color: "var(--secondary)", display: "inline-flex", flexShrink: 0 }}>{c.icon}</span>
                  <span>{c.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Arrow / connector */}
          <div className="pain-arrow" aria-hidden="true">
            <ArrowRight size={22} />
          </div>

          {/* Calm column */}
          <div className="pain-col pain-col-calm">
            <span className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>With PorchLight</span>
            <ul style={{ listStyle: "none", padding: 0, margin: "16px 0 0", display: "flex", flexDirection: "column", gap: 12 }}>
              {calm.map((label, i) => (
                <li
                  key={i}
                  data-testid={`pain-calm-${i}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "14px 18px",
                    background: "rgba(255, 255, 255, 0.06)",
                    border: "1px solid rgba(255, 255, 255, 0.18)",
                    borderRadius: 10,
                    fontSize: 15,
                    color: "#fff",
                    fontWeight: 500,
                  }}
                >
                  <Check size={16} color="#F2EFE9" style={{ flexShrink: 0 }} />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ----- Category positioning — what PorchLight is NOT vs. IS ---
   Sits right after the pain section so visitors who think "is this another
   CRM?" get a clear "no — different category" answer before scrolling into
   features. Two stacked rows (NOT / IS) with the "IS" side carrying the
   emphasis since it's the actual sale.
   ------------------------------------------------------------- */
function CategoryPositioning() {
  const { content } = useMarketing();
  const c = content.category;
  return (
    <section
      id="category"
      data-testid="category-positioning"
      style={{ padding: "112px 0", background: "var(--bg)" }}
    >
      <div className="container-x" style={{ maxWidth: 1080 }}>
        <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 48px" }}>
          <span className="overline" style={{ color: "var(--secondary)" }}>{c.overline}</span>
          <h2
            data-testid="category-headline"
            className="font-serif"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.2rem)", letterSpacing: "-0.02em", margin: "12px 0 18px", fontWeight: 500, lineHeight: 1.08 }}
          >
            {c.eyebrow} {c.headline}{" "}
            <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>{c.headline_emphasis}</em>
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "1.05rem", lineHeight: 1.6, margin: 0 }}>
            {c.body}
          </p>
        </div>

        <div
          className="category-grid"
          data-testid="category-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 24,
            alignItems: "stretch",
          }}
        >
          {/* NOT side — muted/neutral */}
          <div
            data-testid="category-not"
            style={{
              background: "#fff",
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: "32px 28px",
              position: "relative",
            }}
          >
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 28, height: 28, borderRadius: 999,
                  background: "var(--surface-alt)", color: "var(--text-muted)",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <XIcon size={15} />
              </span>
              <span className="overline" style={{ color: "var(--text-muted)" }}>{c.not_label}</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
              {c.not_items.map((it, i) => (
                <li
                  key={i}
                  data-testid={`category-not-${i}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 0",
                    borderBottom: i === c.not_items.length - 1 ? "none" : "1px dashed var(--border)",
                    fontSize: 16,
                    color: "var(--text-muted)",
                    textDecoration: "line-through",
                    textDecorationColor: "rgba(184, 92, 71, 0.4)",
                    textDecorationThickness: "1.5px",
                  }}
                >
                  <span aria-hidden="true" style={{ color: "var(--text-faint)", flexShrink: 0 }}>—</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            <p style={{ marginTop: 20, fontSize: 13, color: "var(--text-faint)", fontStyle: "italic", lineHeight: 1.5 }}>
              {c.not_footnote}
            </p>
          </div>

          {/* IS side — emphasis: primary background, white text */}
          <div
            data-testid="category-is"
            style={{
              background: "var(--primary)",
              color: "#fff",
              border: "1px solid var(--primary)",
              borderRadius: 16,
              padding: "32px 28px",
              position: "relative",
              boxShadow: "0 24px 60px rgba(28, 25, 23, 0.18)",
            }}
          >
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 28, height: 28, borderRadius: 999,
                  background: "rgba(255,255,255,0.18)", color: "#fff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Sparkles size={15} />
              </span>
              <span className="overline" style={{ color: "rgba(255,255,255,0.75)" }}>{c.is_label}</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 14 }}>
              {c.is_items.map((it, i) => (
                <li
                  key={i}
                  data-testid={`category-is-${i}`}
                  className="font-serif"
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 12,
                    padding: "10px 0",
                    borderBottom: i === c.is_items.length - 1 ? "none" : "1px solid rgba(255,255,255,0.14)",
                    fontSize: "1.5rem",
                    lineHeight: 1.25,
                    fontWeight: 500,
                    letterSpacing: "-0.005em",
                  }}
                >
                  <Check size={20} color="#F2EFE9" style={{ flexShrink: 0, marginTop: 4 }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            <p style={{ marginTop: 20, fontSize: 13, color: "rgba(255,255,255,0.78)", lineHeight: 1.5 }}>
              {c.is_footnote}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----- Demo video placeholder -------------------------------- */
function DemoVideo() {
  const { content } = useMarketing();
  const d = content.demo_video;
  return (
    <section id="demo-video" style={{ padding: "64px 0", background: "var(--surface-alt)" }}>
      <div className="container-x" style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 36px" }}>
        <span className="overline" style={{ color: "var(--secondary)" }}>{d.overline}</span>
        <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", letterSpacing: "-0.01em", margin: "12px 0 14px", fontWeight: 500 }}>
          {d.headline}
        </h2>
        <p style={{ color: "var(--text-muted)", fontSize: "1.05rem", lineHeight: 1.6, margin: 0 }}>
          {d.body}
        </p>
      </div>
      <div className="container-x">
        <div
          data-testid="demo-video-frame"
          style={{
            position: "relative",
            aspectRatio: "16 / 9",
            borderRadius: 10,
            overflow: "hidden",
            background: "var(--primary)",
            boxShadow: "0 30px 80px rgba(28, 25, 23, 0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {/* Hero photo behind, dimmed */}
          <img
            src="/prep-guide/exterior.jpg"
            alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.45 }}
          />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(43,76,63,0.45), rgba(43,76,63,0.85))" }} />
          <div style={{ position: "relative", textAlign: "center", color: "#fff", padding: 24 }}>
            <div style={{
              width: 86, height: 86, borderRadius: 999,
              background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.4)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
              cursor: "pointer", transition: "transform 0.18s ease",
            }}>
              <PlayCircle size={42} />
            </div>
            <div style={{ marginTop: 18, fontSize: 14, opacity: 0.9, letterSpacing: "0.04em" }}>
              {d.badge}
            </div>
            <a
              href="#start"
              data-testid="demo-cta"
              style={{
                display: "inline-block", marginTop: 18,
                color: "#fff", borderBottom: "1px solid rgba(255,255,255,0.6)",
                paddingBottom: 2, textDecoration: "none", fontSize: 13, letterSpacing: "0.04em",
              }}
            >
              {d.cta}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----- What it does ----------------------------------------- */
function WhatItDoes() {
  const { content } = useMarketing();
  const w = content.what_it_does;
  const items = (w.items || []).map((label, i) => ({
    icon: WHAT_IT_DOES_ICONS[i] || <Home size={18} />,
    label,
  }));
  return (
    <section id="what-it-does" style={{ padding: "112px 0" }}>
      <div className="container-x" style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.95fr) minmax(0, 1.15fr)", gap: 80, alignItems: "start" }}>
        <div>
          <span className="overline" style={{ color: "var(--secondary)" }}>{w.overline}</span>
          <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", letterSpacing: "-0.01em", margin: "12px 0 18px", fontWeight: 500, lineHeight: 1.1 }}>
            {w.headline}
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "1.05rem", lineHeight: 1.6, margin: 0 }}>
            {w.body}
          </p>
        </div>
        <div data-testid="what-it-does-list" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {items.map((it, i) => (
            <div
              key={i}
              data-testid={`what-item-${i}`}
              style={{
                display: "flex", alignItems: "center", gap: 18,
                padding: "20px 24px",
                borderLeft: "2px solid var(--secondary)",
                background: "var(--surface-alt)",
                borderRadius: "0 12px 12px 0",
              }}
            >
              <span style={{
                width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                background: "#fff", color: "var(--primary)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                border: "1px solid var(--border)",
              }}>{it.icon}</span>
              <span style={{ fontSize: "1.05rem", color: "var(--text)", fontWeight: 500 }}>{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----- And a whole lot more — breadth-of-product icon grid ---- */
function AndMore() {
  const { content } = useMarketing();
  const m = content.more_features;
  const items = (m.items || []).map((it, i) => ({
    icon: MORE_FEATURE_ICONS[i] || <Sparkles size={20} />,
    title: it.title,
    body: it.body,
  }));
  return (
    <section
      id="and-more"
      data-testid="and-more"
      style={{ padding: "112px 0", background: "var(--surface-alt)" }}
    >
      <div className="container-x">
        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 56px" }}>
          <span className="overline" style={{ color: "var(--secondary)" }}>{m.overline}</span>
          <h2 className="font-serif" style={{ fontSize: "clamp(1.9rem, 4vw, 2.8rem)", letterSpacing: "-0.02em", margin: "12px 0 16px", fontWeight: 500, lineHeight: 1.1 }}>
            {m.headline}{m.headline_italic ? <> <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>{m.headline_italic}</em></> : null}
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "1.05rem", lineHeight: 1.6, margin: 0 }}>
            {m.body}
          </p>
        </div>

        <div className="and-more-grid" data-testid="and-more-grid">
          {items.map((f, i) => (
            <div
              key={i}
              data-testid={`and-more-${i}`}
              className="and-more-card"
            >
              <span className="and-more-icon" aria-hidden="true">{f.icon}</span>
              <h3 className="and-more-title">{f.title}</h3>
              <p className="and-more-body">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ----- Reusable feature spotlight — used for Prep Guide, Net Sheet, Dates ----- */
function FeatureSpotlight({ spotlight, spotlightIndex, onOpen }) {
  const { id, overline, headline, italicTail, body, features, background, imageSide, shots } = spotlight;
  const [idx, setIdx] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const total = shots.length;
  const hasCarousel = total > 1;

  // Auto-rotate every 4.5s when idle; pause on hover.
  React.useEffect(() => {
    if (!hasCarousel || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 4500);
    return () => clearInterval(t);
  }, [paused, hasCarousel, total]);

  // Render the headline with the italicTail (if any) styled in italic + accent.
  const renderHeadline = () => {
    if (italicTail && headline.endsWith(italicTail)) {
      const head = headline.slice(0, headline.length - italicTail.length);
      return (
        <>
          {head}<em style={{ fontStyle: "italic", color: "var(--secondary)" }}>{italicTail}</em>
        </>
      );
    }
    return headline;
  };

  const reverse = imageSide === "left";

  const TextSide = (
    <div>
      <span className="overline" style={{ color: "var(--secondary)" }}>{overline}</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4.2vw, 3rem)", letterSpacing: "-0.02em", margin: "14px 0 18px", fontWeight: 500, lineHeight: 1.08 }}>
        {renderHeadline()}
      </h2>
      <p style={{ color: "var(--text-muted)", fontSize: "1.05rem", lineHeight: 1.6, margin: "0 0 28px" }}>
        {body}
      </p>
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {features.map((f) => (
          <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14 }}>
            <Check size={16} color="var(--primary)" style={{ flexShrink: 0, marginTop: 3 }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {hasCarousel && (
        <div className="prep-spotlight-tabs" data-testid={`spotlight-${spotlightIndex}-tabs`}>
          {shots.map((s, i) => (
            <button
              key={s.label}
              type="button"
              data-testid={`spotlight-${spotlightIndex}-tab-${i}`}
              onClick={() => { setIdx(i); setPaused(true); }}
              className={"prep-spotlight-tab" + (i === idx ? " active" : "")}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const VisualSide = (
    <div
      className="prep-spotlight-visual"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <button
        type="button"
        data-testid={`spotlight-${spotlightIndex}-open`}
        onClick={() => onOpen(idx)}
        aria-label={`Enlarge: ${overline}`}
        className="prep-spotlight-frame prep-spotlight-frame-button"
      >
        {shots.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={`${overline} — ${s.label}`}
            className={"prep-spotlight-img" + (i === idx ? " active" : "")}
            loading="lazy"
          />
        ))}
        <span className="screenshot-zoom-hint" aria-hidden="true">Click to enlarge</span>
      </button>
      {hasCarousel && (
        <span className="prep-spotlight-badge" aria-hidden="true">
          <Sparkles size={13} /> {shots[idx].label}
        </span>
      )}
    </div>
  );

  return (
    <section
      id={id}
      data-testid={`spotlight-${spotlightIndex}`}
      style={{ padding: "112px 0", background }}
    >
      <div className="container-x prep-spotlight-grid" style={{ gridTemplateColumns: total === 0 ? "minmax(0, 820px)" : reverse ? "minmax(0, 1.15fr) minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1.15fr)" }}>
        {total === 0 ? TextSide : reverse ? <>{VisualSide}{TextSide}</> : <>{TextSide}{VisualSide}</>}
      </div>
    </section>
  );
}

function Lightbox({ card, initialShotIndex = 0, onClose }) {
  const [idx, setIdx] = useState(initialShotIndex);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [fitImage, setFitImage] = useState(false);
  const scrollRef = React.useRef(null);
  const total = card.shots.length;
  const shot = card.shots[idx];
  const hasCarousel = total > 1;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (hasCarousel && e.key === "ArrowRight") setIdx((i) => (i + 1) % total);
      if (hasCarousel && e.key === "ArrowLeft") setIdx((i) => (i - 1 + total) % total);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, hasCarousel, total]);

  // After image loads, decide whether to show the "scroll for more" hint.
  // Reset scroll position whenever the active shot changes.
  const onImgLoad = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    // Scaling the shot to the frame height is only sensible while it
    // stays wide enough to read. Below that it is a tall screenshot and
    // scrolling is the point.
    const img = el.querySelector('img');
    const ratio = img && img.naturalHeight
      ? img.naturalWidth / img.naturalHeight : 0;
    const canFit = ratio > 0
      && el.clientHeight * ratio >= el.clientWidth * 0.6;
    setFitImage(canFit);
    setShowScrollHint(!canFit && el.scrollHeight > el.clientHeight + 40);
  };
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop > 30 && showScrollHint) setShowScrollHint(false);
  };

  return (
    <div
      data-testid="screenshot-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={card.caption}
      onClick={onClose}
      className="lightbox-backdrop"
    >
      <button type="button" data-testid="lightbox-close" onClick={onClose} className="lightbox-close" aria-label="Close">
        <XIcon size={20} />
      </button>
      <figure onClick={(e) => e.stopPropagation()} className="lightbox-figure">
        <div className="screenshot-frame" style={{ width: "100%", position: "relative" }}>
          <div ref={scrollRef} onScroll={onScroll} className={"lightbox-image-scroll" + (fitImage ? " is-fit" : "")} data-testid="lightbox-image-scroll">
            <img
              key={shot.src}
              src={shot.src}
              alt={`${card.caption} — ${shot.label}`}
              onLoad={onImgLoad}
            />
          </div>
          {showScrollHint && (
            <span className="lightbox-scroll-hint" data-testid="lightbox-scroll-hint" aria-hidden="true">
              Scroll for more ↓
            </span>
          )}
          {hasCarousel && (
            <>
              <button type="button" className="carousel-arrow carousel-arrow-prev lightbox-arrow"
                onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + total) % total); }}
                aria-label="Previous" data-testid="lightbox-prev">‹</button>
              <button type="button" className="carousel-arrow carousel-arrow-next lightbox-arrow"
                onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % total); }}
                aria-label="Next" data-testid="lightbox-next">›</button>
            </>
          )}
        </div>
        <figcaption className="lightbox-caption">
          <span className="screenshot-tag" style={{ color: "#F2EFE9" }}>
            {card.caption}{hasCarousel && ` — ${shot.label}`}
          </span>
          <span style={{ color: "#fff", fontSize: 16, fontWeight: 500 }}>{card.emphasis}</span>
          {hasCarousel && (
            <div className="carousel-dots lightbox-dots" data-testid="lightbox-dots">
              {card.shots.map((s, j) => (
                <button
                  key={j}
                  type="button"
                  className={"carousel-dot" + (j === idx ? " active" : "")}
                  onClick={(e) => { e.stopPropagation(); setIdx(j); }}
                  aria-label={`Go to ${s.label}`}
                />
              ))}
            </div>
          )}
        </figcaption>
      </figure>
    </div>
  );
}

/* ----- Why agents use it ------------------------------------ */
function WhyAgentsUse() {
  const { content } = useMarketing();
  const w = content.why_agents;
  const reasons = w.reasons || [];
  return (
    <section id="why-agents" style={{ padding: "112px 0", background: "var(--primary)", color: "#fff" }}>
      <div className="container-x" style={{ maxWidth: 980 }}>
        <span className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>{w.overline}</span>
        <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", letterSpacing: "-0.01em", margin: "14px 0 36px", fontWeight: 500, lineHeight: 1.08 }}>
          {w.headline}{w.headline_italic ? <> <em style={{ fontStyle: "italic", color: "#F2EFE9" }}>{w.headline_italic}</em></> : null}
        </h2>
        <div data-testid="why-list" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}>
          {reasons.map((r, i) => (
            <div
              key={i}
              data-testid={`why-item-${i}`}
              style={{
                display: "flex", alignItems: "flex-start", gap: 14,
                padding: 24, background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8,
              }}
            >
              <Sparkles size={20} color="#F2EFE9" style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: "1.05rem", lineHeight: 1.5 }}>{r}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----- Pricing ---------------------------------------------- */
function Pricing() {
  const { content } = useMarketing();
  const p = content.pricing;
  const left = p.features_left || [];
  const right = p.features_right || [];
  const [annual, setAnnual] = useState(false);
  const toggleBtn = (active) => ({
    border: "none", cursor: "pointer", borderRadius: 999,
    padding: "9px 20px", fontSize: 14, fontWeight: 600,
    fontFamily: "inherit",
    background: active ? "var(--primary)" : "transparent",
    color: active ? "#fff" : "var(--text-muted)",
    transition: "background-color 0.2s ease, color 0.2s ease",
  });
  return (
    <section id="pricing" style={{ padding: "112px 0" }}>
      <div className="container-x" style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
        <span className="overline" style={{ color: "var(--secondary)" }}>{p.overline}</span>
        <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", letterSpacing: "-0.01em", margin: "12px 0 14px", fontWeight: 500 }}>
          {p.headline}{p.headline_italic ? <> <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>{p.headline_italic}</em></> : null}
        </h2>
        <p style={{ color: "var(--text-muted)", fontSize: "1.05rem", lineHeight: 1.6, margin: "0 0 28px", whiteSpace: "pre-line" }}>
          {p.body}
        </p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 2, padding: 4, borderRadius: 999, border: "1px solid var(--border)", background: "var(--surface-alt)", marginBottom: 28 }}>
          <button type="button" data-testid="pricing-toggle-monthly" style={toggleBtn(!annual)} onClick={() => setAnnual(false)}>
            Monthly
          </button>
          <button type="button" data-testid="pricing-toggle-annual" style={toggleBtn(annual)} onClick={() => setAnnual(true)}>
            Annual <span style={{ fontWeight: 500, opacity: 0.85 }}>· save 2 months</span>
          </button>
        </div>
        <div data-testid="pricing-price" style={{ marginBottom: 30 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 8 }}>
            <span className="font-serif" style={{ fontSize: "clamp(2.6rem, 5vw, 3.4rem)", fontWeight: 500, letterSpacing: "-0.02em" }}>
              {annual ? "$490" : "$49"}
            </span>
            <span style={{ color: "var(--text-muted)", fontSize: "1.05rem" }}>{annual ? "/ year" : "/ month"}</span>
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "8px 0 0" }} data-testid="pricing-billing-note">
            {annual
              ? "Two months free."
              : "Billed monthly. Cancel anytime."}
          </p>
        </div>
        <div
          data-testid="pricing-card"
          style={{
            background: "var(--surface-alt)",
            border: "1px solid var(--border)",
            borderRadius: 14, padding: 36,
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 36,
            textAlign: "left",
          }}
        >
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            {left.map((f) => (
              <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14 }}>
                <Check size={16} color="var(--primary)" style={{ flexShrink: 0, marginTop: 3 }} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            {right.map((f) => (
              <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14 }}>
                <Check size={16} color="var(--primary)" style={{ flexShrink: 0, marginTop: 3 }} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
        <a
          href="#start"
          data-testid="pricing-cta"
          className="btn-primary"
          style={{ marginTop: 32, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          {p.cta} <ArrowRight size={16} />
        </a>
      </div>
    </section>
  );
}

/* ----- FAQ — answers the questions agents have right before signing up ----- */
function FAQ() {
  const { content } = useMarketing();
  const f = content.faq;
  const items = f.items || [];
  const [open, setOpen] = useState(0); // first one open by default
  return (
    <section
      id="faq"
      data-testid="faq-section"
      style={{ padding: "112px 0" }}
    >
      <div className="container-x" style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.8fr) minmax(0, 1.2fr)", gap: 80, alignItems: "start" }}>
        <div>
          <span className="overline" style={{ color: "var(--secondary)" }}>{f.overline}</span>
          <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", letterSpacing: "-0.02em", margin: "14px 0 18px", fontWeight: 500, lineHeight: 1.08 }}>
            {f.headline}{f.headline_italic ? <> <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>{f.headline_italic}</em></> : null}
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "1rem", lineHeight: 1.6, margin: "0 0 18px" }}>
            {f.body}
          </p>
          <a href="#start" className="marketing-nav-link" style={{ fontSize: 14 }}>
            {f.cta}
          </a>
        </div>

        <div data-testid="faq-list" style={{ display: "flex", flexDirection: "column" }}>
          {items.map((item, i) => {
            const isOpen = i === open;
            return (
              <div key={i} className="faq-item" data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="faq-toggle"
                  aria-expanded={isOpen}
                >
                  <span style={{ fontSize: 16, fontWeight: 500, color: "var(--text)", flex: 1, textAlign: "left", lineHeight: 1.4 }}>
                    {item.q}
                  </span>
                  <ChevronDown size={18} className="faq-chev" style={{ transform: isOpen ? "rotate(180deg)" : "none", color: "var(--secondary)", flexShrink: 0, transition: "transform 0.2s ease" }} />
                </button>
                <div className={"faq-answer" + (isOpen ? " open" : "")}>
                  <p style={{ color: "var(--text-muted)", fontSize: 15, lineHeight: 1.7, margin: 0, padding: "0 0 22px" }}>
                    {item.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


/* ----- Free trial CTA (replaces old waitlist form now that
 *       /signup is open and trial-onboarding is live) ---------- */
function Waitlist() {
  const { content } = useMarketing();
  const w = content.waitlist || {};
  const headline = w.cta_headline || "Ready to give your sellers a better experience?";
  const body = w.cta_body || "Your first listing is free, start to finish. No setup fees, no credit card required up front. Bring your logo, your colors, your voice - your sellers see your brand from the moment they log in.";
  const bullets = w.cta_bullets || [
    "Branded seller portal — logo, colors, voice",
    "Room-by-room prep, live net sheet, offer comparison",
    "Auto-emailed key-date reminders",
    "Cancel anytime · keep your data",
  ];
  return (
    <section id="start" data-testid="trial-cta-section" style={{ padding: "112px 0", background: "var(--surface-alt)" }}>
      <div className="container-x" style={{ maxWidth: 720, margin: "0 auto" }}>
        <div
          data-testid="trial-cta-card"
          style={{
            background: "#fff",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: "48px 40px",
            textAlign: "center",
            boxShadow: "0 12px 40px rgba(28,25,23,0.04)",
          }}
        >
          <span className="overline" style={{ color: "var(--secondary)" }}>Start free</span>
          <h2 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.6rem)", letterSpacing: "-0.01em", margin: "12px 0 14px", fontWeight: 500, lineHeight: 1.15 }}>
            {headline}
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "1.05rem", lineHeight: 1.6, margin: "0 auto 28px", maxWidth: 540 }}>
            {body}
          </p>

          <ul
            data-testid="trial-cta-bullets"
            style={{
              listStyle: "none",
              padding: 0,
              margin: "0 auto 32px",
              maxWidth: 480,
              display: "grid",
              gap: 10,
              textAlign: "left",
            }}
          >
            {bullets.map((b, i) => (
              <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 15, color: "var(--text)" }}>
                <Check size={18} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} />
                {b}
              </li>
            ))}
          </ul>

          <Link
            to="/signup"
            data-testid="trial-cta-primary"
            className="btn-primary"
            style={{ textDecoration: "none", padding: "14px 32px", fontSize: 15, display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            Start your first listing <ArrowRight size={16} />
          </Link>
          <p style={{ fontSize: 12, color: "var(--text-faint)", margin: "16px 0 0" }}>
            First listing free · No credit card · Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
}

/* ----- Footer ----------------------------------------------- */
function MarketingFooter() {
  const { content } = useMarketing();
  const tagline = content.footer?.tagline || "PorchLight";
  return (
    <footer style={{ padding: "48px 0 64px", borderTop: "1px solid var(--border)" }}>
      <div className="container-x" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <span style={{
            width: 26, height: 26, borderRadius: 6,
            background: "var(--primary)", color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Home size={14} />
          </span>
          <span className="font-serif" style={{ fontSize: 18, fontWeight: 500 }}>{tagline}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 24, fontSize: 13, color: "var(--text-muted)" }}>
          <a href="#features" className="marketing-nav-link">Product</a>
          <a href="#pricing" className="marketing-nav-link">Pricing</a>
          <a href="#features" className="marketing-nav-link">See what it looks like</a>
          <a href="/login" className="marketing-nav-link">Agent login</a>
        </div>
        <span style={{ fontSize: 12, color: "var(--text-faint)" }}>© {new Date().getFullYear()} {tagline} · theporchlight.io</span>
      </div>
    </footer>
  );
}
